import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, Signal, computed, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { OfferSource } from '@cds';
import { DisplayType, Parameter } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOptionGroup, EventOptionGroupType, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { MarketSubType, MarketType, PeriodType } from '@frontend/sports/betting-offer/feature/types';
import {
    BetBuilderConfig,
    EventDetailsConfig,
    LayoutNavigationConfig,
    MarketGroupingConfig,
    MarketHelpConfig,
    Sitecore,
} from '@frontend/sports/common/client-config-data-access';
import { injectCurrentTheme } from '@frontend/sports/common/ui/shared';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { DeviceService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { NgxFloatUiPlacements } from 'ngx-float-ui';

import { MarketHeaderBetBuilderBadgeComponent } from '../betbuilder/components/badge/market-header-badge/market-header-bet-builder-badge.component';
import { SGPBadgeComponent } from '../betbuilder/components/badge/sgp-badge/sgp-badge.component';
import { BetBuilderBadgeType } from '../betbuilder/model/bet-builder.model';
import { isProviderSupportingBadges } from '../betbuilder/utils';
import { EpcotConfigService } from '../common/epcot-config.service';
import { MarketSubtypeLogoComponent } from '../marketsubtype-logo/marketsubtype-logo.component';
import { MarketSubTypeLogoService } from '../marketsubtype-logo/marketsubtype-logo.service';
import { TimerModule } from '../timers/timer.module';
import { EachWayMarketHelperService } from './each-way-market-helper.service';
import { ExtendedOfferComponent } from './extended-offer.component';
import { MarketHelpToggleComponent } from './market-help/market-help-toggle.component';
import { ToggleViewButtonComponent } from './toggle-view-button.component';

@Component({
    selector: 'ms-option-panel-header',
    templateUrl: './option-panel-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        TimerModule,
        IconCustomComponent,
        MarketHelpToggleComponent,
        ExtendedOfferComponent,
        MarketSubtypeLogoComponent,
        ToggleViewButtonComponent,
        MarketHeaderBetBuilderBadgeComponent,
        SGPBadgeComponent,
    ],
})
export class OptionPanelHeaderComponent implements OnChanges {
    @Input() groupType: OptionGroupType;
    @Input() optionGroup: EventOptionGroup;
    @Input() showHelp: boolean;
    @Input() toggleable: boolean;
    @Input() collapsed: boolean;
    @Input() activePeriodTab?: string;
    @Input() promoted: boolean;
    @Input() showMarketHelp: boolean;
    @Input() eventHelpText?: string;
    @Input() event: EventModel;
    @Input() marketLogoShow: boolean;
    @Input() showEventName = false;
    @Input() showEventTimer = false;
    @Input() showEventChevron = false;
    @Input() isLinkToEvent = true;
    @Input() shouldShowCloseBtn?: boolean;
    @Input() appendTooltipToWidgetSlot = false;
    @Output() collapsedChange = new EventEmitter<boolean>();
    @Output() showMarketHelpChange = new EventEmitter<boolean>();
    @Output() eventTitleClicked = new EventEmitter<void>();
    @Output() closeBtnClicked = new EventEmitter<void>();

    showPrecreatedBetBuilderNewBadge = false;
    enableCorrectScoreOptionGroupToggle = false;
    showMarketHelpToggle: boolean;
    isPrecreatedBuildABet: boolean;
    winMarketName?: string;
    shouldShowChevron: boolean;
    isEpcotEnabled: boolean;
    isPrecreatedBetBuilderBadgeEnabled: boolean;
    showSuspendedSelection: boolean;
    marketType?: MarketType;
    marketSubType?: MarketSubType;
    templateId?: string;
    marketLogoShowInHeader?: boolean;
    tooltipAppendto?: string;

    isBetMgmTheme: boolean = injectCurrentTheme() === 'mgm-4';
    protected isBetBuilderBadgeVisible = signal<boolean>(false);

    constructor(
        private marketHelpConfig: MarketHelpConfig,
        private redirectHelperService: RedirectHelperService,
        public sitecore: Sitecore,
        public betBuilderConfig: BetBuilderConfig,
        public eventDetailsConfig: EventDetailsConfig,
        private epcotConfigService: EpcotConfigService,
        private eachWayMarketHelperService: EachWayMarketHelperService,
        private marketSubTypeLogoService: MarketSubTypeLogoService,
        public deviceService: DeviceService,
        private marketGroupingConfig: MarketGroupingConfig,
        private layoutConfig: LayoutNavigationConfig,
        private route: ActivatedRoute,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
        this.showSuspendedSelection = this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled;
    }

    getBadgeTypeToShow: Signal<BetBuilderBadgeType | undefined> = computed(() => {
        if (this.isBetMgmTheme) {
            return (this.eventDetailsConfig.isBetBuilderSignpostingBadgeEnabled && this.isPrecreatedBuildABet) || this.isBetBuilderBadgeVisible()
                ? BetBuilderBadgeType.SGP
                : undefined;
        }
        return this.isBetBuilderBadgeVisible() ? BetBuilderBadgeType.BetBuilder : undefined;
    });

    ngOnChanges(): void {
        const tooltipAppendTo = this.appendTooltipToWidgetSlot ? 'ms-widget-slot' : 'ms-event-details-main';
        this.tooltipAppendto =
            this.layoutConfig.isDynamicCategoryNavigationEnabled && !!this.route.snapshot.queryParams.dynamicCategory
                ? 'ms-fixture-list'
                : tooltipAppendTo;
        this.isPrecreatedBuildABet = this.groupType === OptionGroupType.PrecreatedBuildABet;
        if (this.isPrecreatedBuildABet && this.sitecore.betBuilder.messages) {
            this.showPrecreatedBetBuilderNewBadge = Boolean(
                this.event.sport && this.betBuilderConfig.isPrecreatedBetBuilderNewBadgeEnabledForSport?.includes(this.event.sport.id),
            );
            const sportSpecificThemedTabs = this.betBuilderConfig.themedTabs[this.event.sport.id];
            const themedTabName = sportSpecificThemedTabs?.find((i) => i.key?.toUpperCase() === this.optionGroup.periodName?.toUpperCase())?.name;
            if (!this.optionGroup.subGroups && themedTabName?.trim() !== '' && themedTabName !== undefined) {
                this.optionGroup.name = this.sitecore.betBuilder.messages.PrecreatedBuildABetTitle.concat(' - ', themedTabName);
            } else {
                this.optionGroup.name = this.sitecore.betBuilder.messages.PrecreatedBuildABetTitle;
            }
        }
        this.setMarketHelpToggle();
        this.setMarketParameters();

        this.winMarketName = this.eachWayMarketHelperService.getWinMarketName(this.optionGroup, this.event);

        this.shouldShowChevron = this.collapsed || this.eventDetailsConfig.collapsibleChevron;
        this.isPrecreatedBetBuilderBadgeEnabled = this.betBuilderConfig.isPrecreatedBetBuilderBadgeEnabled;
        this.setOptionGroupName();
        this.isBetBuilderBadgeVisible.set(
            this.eventDetailsConfig.isBetBuilderSignpostingBadgeEnabled &&
                this.optionGroup.hasBetBuilderMarkets &&
                isProviderSupportingBadges(this.event.betBuilderInfo),
        );
    }

    private setMarketHelpToggle(): void {
        if (this.eventHelpText) {
            this.showMarketHelpToggle = true;

            return;
        }
        this.showMarketHelpToggle = this.showHelp && this.marketHelpConfig.enabled;

        if (!this.showMarketHelpToggle) {
            return;
        }

        if (this.groupType === OptionGroupType.Period && this.optionGroup.subGroups) {
            this.showMarketHelpToggle = this.optionGroup.subGroups.some((group) => this.hasHelpPath(group));
        } else {
            this.showMarketHelpToggle = this.hasHelpPath(this.optionGroup);
        }
    }

    private setMarketParameters(): void {
        const parameters = this.getParameters();
        this.marketSubType = parameters?.find((p) => p.key === Tv2MarketParameters.MarketSubType)?.value as MarketSubType;
        this.marketType = parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value as MarketType;

        if (this.optionGroup.groupedMarkets && this.optionGroup.groupedMarkets.length > 0) {
            const isGroupedTemplateIdNotInSecondChanceBadge = !this.marketGroupingConfig.templateIdsForSecondChanceBadge.includes(
                Number(this.optionGroup.templateId),
            );

            this.templateId = isGroupedTemplateIdNotInSecondChanceBadge
                ? this.optionGroup.groupedTemplateIds?.find((id) => this.marketGroupingConfig.templateIdsForSecondChanceBadge.includes(Number(id))) ||
                  this.optionGroup.templateId
                : this.optionGroup.templateId;
        } else {
            this.templateId = this.optionGroup.templateId;
        }

        this.marketLogoShowInHeader =
            this.marketLogoShow &&
            !!this.marketSubTypeLogoService.getMarketSubTypeLogoTitle(
                this.event.id,
                this.event.sport.id,
                this.marketType,
                this.marketSubType,
                this.templateId,
            );
        const periodType = parameters?.find((p) => p.key === Tv2MarketParameters.Period)?.value as PeriodType;
        this.enableCorrectScoreOptionGroupToggle =
            this.eventDetailsConfig.enableCorrectScoreOptionGroupToggle &&
            this.optionGroup.detailedGrouping.displayType === DisplayType.CorrectScore &&
            periodType === PeriodType.RegularTime;
    }

    handleHeaderClick(event$: Event): void {
        if (this.showEventChevron) {
            this.goToEvent(event$);
        } else if (this.toggleable) {
            this.collapsed = !this.collapsed && (this.showSuspendedSelection || this.optionGroup.online);
            this.collapsedChange.emit(this.collapsed);
            this.setShowMarketHelp(false);
        }
    }

    toggleMarketHelp(value: boolean): void {
        this.setShowMarketHelp(value);
    }

    private setShowMarketHelp(value: boolean): void {
        this.showMarketHelp = value;
        this.showMarketHelpChange.next(value);
    }

    private hasHelpPath(optionGroup: EventOptionGroup): boolean {
        return !!optionGroup.detailedGrouping && !!optionGroup.detailedGrouping.marketHelpPath;
    }

    goToEvent(event$: Event): void {
        event$.stopPropagation();
        if (this.isLinkToEvent) {
            this.eventTitleClicked.emit();
            this.redirectHelperService.goToEvent(this.event);
        }
    }

    closePanel(): void {
        this.closeBtnClicked.emit();
    }

    private setOptionGroupName(): void {
        // For PlayerProps display type with V1 offer source show the period tab as market header
        if (
            this.event.offerSource === OfferSource.V1 &&
            this.optionGroup.detailedGrouping.displayType === DisplayType.PlayerProps &&
            this.optionGroup.periodName &&
            !this.optionGroup.hasSubGroups(EventOptionGroupType.Period)
        ) {
            this.optionGroup.name = this.optionGroup.periodName;
        }
    }

    private getParameters(): Parameter[] | undefined {
        if (this.activePeriodTab) {
            return this.optionGroup.subGroups?.find((sg) => sg.originalId === this.activePeriodTab)?.parameters;
        } else {
            return this.optionGroup.parameters;
        }
    }

    protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
}
